import { useCallback, useEffect, useState } from "react";
import { FavouriteStrategy, api, isError } from "../api";

/** Backtest > Favourites: the operator's curated list of tested strategies.
 *  Each row is a strategy saved from a completed run, with the metrics of
 *  the run it was validated on. Trading-config's convergence picker draws
 *  from this list. */
export function FavouritesPanel() {
  const [favs, setFavs] = useState<FavouriteStrategy[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    const r = await api.backtestFavourites();
    if (isError(r)) {
      setError(r.error);
      return;
    }
    setError(null);
    setFavs(r.favourites);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function remove(f: FavouriteStrategy) {
    if (!window.confirm(`Remove ${f.strategy_id} from favourites? (Backtest runs are kept.)`)) return;
    setBusyId(f.id);
    const r = await api.backtestDeleteFavourite(f.id);
    setBusyId(null);
    if (isError(r)) {
      setError(r.error);
      return;
    }
    setFavs(r.favourites);
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-fg-muted">
        Strategies you have backtested and chosen to keep. Save one from a
        completed <span className="font-mono">single</span> run with the
        ★ button; its <span className="font-mono">strategy_id</span> is what
        you put in the TradingView alert, and Trading-config → Convergence
        picks from this list.
      </p>
      {error && (
        <p className="rounded-md border border-danger/40 bg-danger/10 px-2 py-1 text-xs text-danger">
          {error}
        </p>
      )}
      {favs === null ? (
        <p className="text-xs text-fg-muted">Loading…</p>
      ) : favs.length === 0 ? (
        <p className="text-sm text-fg-muted">
          No favourites yet. Run a strategy on the Single tab and click ★ save as favourite.
        </p>
      ) : (
        <table className="w-full text-xs font-mono">
          <thead className="text-fg-muted">
            <tr>
              <th className="text-left p-1">strategy_id</th>
              <th className="text-left p-1">backtester</th>
              <th className="text-left p-1">params</th>
              <th className="text-left p-1">validated on</th>
              <th className="text-right p-1">trades</th>
              <th className="text-right p-1">win%</th>
              <th className="text-right p-1">PF</th>
              <th className="text-right p-1">exp.</th>
              <th className="text-right p-1">P/L</th>
              <th className="text-right p-1">maxDD</th>
              <th className="text-left p-1">note</th>
              <th className="p-1" />
            </tr>
          </thead>
          <tbody>
            {favs.map((f) => {
              const v = f.validated;
              return (
                <tr key={f.id} className="border-t border-border align-top">
                  <td className="p-1 font-bold text-fg">★ {f.strategy_id}</td>
                  <td className="p-1">{f.strategy_name}</td>
                  <td className="p-1 text-fg-muted">
                    {Object.entries(f.params)
                      .map(([k, val]) => `${k}=${val}`)
                      .join(" ")}
                  </td>
                  <td className="p-1">
                    {v ? `${v.symbol} ${v.timeframe} · ${v.bars.toLocaleString()} bars` : "run deleted"}
                  </td>
                  <td className="p-1 text-right">{v?.trades ?? "—"}</td>
                  <td className="p-1 text-right">
                    {v?.win_rate != null ? `${Math.round(v.win_rate * 100)}%` : "—"}
                  </td>
                  <td className={`p-1 text-right ${pfTone(v?.profit_factor)}`}>
                    {v?.profit_factor != null ? v.profit_factor.toFixed(2) : "—"}
                  </td>
                  <td className={`p-1 text-right ${signTone(v?.expectancy)}`}>
                    {v?.expectancy != null ? signed(v.expectancy, 2) : "—"}
                  </td>
                  <td className={`p-1 text-right ${signTone(v?.total_pnl)}`}>
                    {v?.total_pnl != null ? signed(v.total_pnl, 2) : "—"}
                  </td>
                  <td className="p-1 text-right text-danger">
                    {v?.max_drawdown_pct != null ? `${v.max_drawdown_pct.toFixed(1)}%` : "—"}
                  </td>
                  <td className="p-1 text-fg-muted">{f.note ?? ""}</td>
                  <td className="p-1 text-right">
                    <button
                      onClick={() => void remove(f)}
                      disabled={busyId === f.id}
                      className="rounded border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-danger/10 hover:text-danger disabled:opacity-50"
                    >
                      remove
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Inline "★ save as favourite" control shown under a completed single run. */
export function SaveFavouriteButton({
  jobId,
  strategyName,
  onSaved,
}: {
  jobId: number;
  strategyName: string;
  onSaved?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [strategyId, setStrategyId] = useState(strategyName);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    setStrategyId(strategyName);
    setMsg(null);
    setOpen(false);
  }, [jobId, strategyName]);

  async function save() {
    setBusy(true);
    setMsg(null);
    const r = await api.backtestSaveFavourite(jobId, strategyId.trim(), note.trim() || undefined);
    setBusy(false);
    if (isError(r)) {
      setMsg(`Could not save: ${r.error}`);
      return;
    }
    setMsg(`Saved ★ ${r.favourite.strategy_id} — pick it on Trading-config → Convergence.`);
    setOpen(false);
    onSaved?.();
  }

  return (
    <div className="flex flex-col gap-1">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="w-fit rounded border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          ★ save as favourite
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <label className="text-fg-muted">strategy_id</label>
          <input
            type="text"
            value={strategyId}
            onChange={(e) => setStrategyId(e.target.value)}
            className="w-44 rounded border border-border bg-page px-2 py-1 font-mono text-fg outline-none focus:border-primary"
            placeholder="ema_trend_m15"
          />
          <label className="text-fg-muted">note</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-64 rounded border border-border bg-page px-2 py-1 text-fg outline-none focus:border-primary"
            placeholder="e.g. EURUSD M15, 10 months, PF 1.16"
          />
          <button
            onClick={() => void save()}
            disabled={busy || !strategyId.trim()}
            className="rounded bg-primary px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-white disabled:opacity-50"
          >
            {busy ? "saving…" : "save"}
          </button>
          <button
            onClick={() => setOpen(false)}
            className="rounded border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted"
          >
            cancel
          </button>
        </div>
      )}
      {msg && <p className="text-xs text-fg-muted">{msg}</p>}
    </div>
  );
}

function signed(v: number, dp: number): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(dp)}`;
}

function signTone(v: number | null | undefined): string {
  if (v == null) return "";
  return v >= 0 ? "text-success" : "text-danger";
}

function pfTone(v: number | null | undefined): string {
  if (v == null) return "";
  return v >= 1 ? "text-success" : "text-danger";
}
